import React from 'react';

/**
 * Binary toggle component (ACCEPT/DECLINE style)
 * Design: High-contrast, rectangular buttons
 */
export default function Toggle({
  value,
  onChange,
  options = [
    { value: true, label: 'ACCEPT' },
    { value: false, label: 'DECLINE' },
  ],
  disabled = false,
  className = '',
}) {
  return (
    <div className={`inline-flex border-2 border-black ${className}`}>
      {options.map((option, index) => (
        <button
          key={option.value.toString()}
          type="button"
          disabled={disabled}
          onClick={() => onChange(option.value)}
          className={`
            px-6 py-3
            font-mono font-semibold text-sm uppercase tracking-wider
            transition-colors duration-200
            disabled:opacity-50 disabled:cursor-not-allowed
            ${index > 0 ? 'border-l-2 border-black' : ''}
            ${
              value === option.value
                ? 'bg-black text-white'
                : 'bg-white text-black hover:bg-gray-100'
            }
          `}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Simple on/off toggle switch variant
 * Supports both event-style onChange (with name prop) and direct value onChange
 */
export function ToggleSwitch({
  name,
  checked,
  onChange,
  label,
  disabled = false,
  className = '',
}) {
  const handleClick = () => {
    const newValue = !checked;
    // If name is provided, emit event-like object for form handling
    if (name && typeof onChange === 'function') {
      onChange({
        target: {
          name,
          type: 'checkbox',
          checked: newValue,
          value: newValue,
        },
      });
    } else {
      // Direct value callback
      onChange(newValue);
    }
  };

  return (
    <label className={`inline-flex items-center gap-3 cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={handleClick}
        className={`
          relative w-14 h-8
          border-2 border-black
          transition-colors duration-200
          ${checked ? 'bg-black' : 'bg-white'}
        `}
      >
        <span
          className={`
            absolute top-1 left-1
            w-5 h-5
            border-2 border-black
            transition-transform duration-200
            ${checked ? 'translate-x-6 bg-white' : 'translate-x-0 bg-black'}
          `}
        />
      </button>
      {label && (
        <span className="font-mono text-sm font-semibold uppercase tracking-wider text-black">
          {label}
        </span>
      )}
    </label>
  );
}
